import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Send, Search, ShieldCheck, Lock, ArrowLeft, MessageSquare, Users as UsersIcon } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  getOrCreateKeyPair, encryptForRecipient, decryptFromSender, shortFingerprint,
} from "../lib/e2e";

/**
 * Page messagerie privée E2E (chiffrement bout-en-bout).
 * - Génère une paire de clés locale au premier accès, publie la clé publique au serveur
 * - Liste les contacts, ouvre une conversation, envoie / reçoit des messages chiffrés
 */
export default function MessagesPage() {
  const { user } = useAuth();
  const [kp, setKp] = useState(null);
  const [directory, setDirectory] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]); // decrypted [{id, sender_id, text|ciphertext, sent_at}]
  const [draft, setDraft] = useState("");
  const [search, setSearch] = useState("");
  const [otherKey, setOtherKey] = useState(null);
  const bottomRef = useRef(null);

  // 1. Init keypair + publish
  useEffect(() => {
    const k = getOrCreateKeyPair();
    setKp(k);
    api.post("/members/me/keys", { public_key: k.publicKeyB64 }).catch(() => {});
  }, []);

  // 2. Load directory & conversations
  useEffect(() => {
    api.get("/members/directory").then((r) => setDirectory(r.data.filter((u) => u.id !== user?.id)));
    refreshConversations();
  }, [user]);

  const refreshConversations = () => api.get("/conversations").then((r) => setConversations(r.data));

  // 3. Open conversation
  const openConv = async (conv, otherUser) => {
    let c = conv;
    if (!c && otherUser) {
      const r = await api.post("/conversations", { participant_id: otherUser.id });
      c = r.data;
      refreshConversations();
    }
    setActiveConv(c);
    if (otherUser) {
      setOtherKey(otherUser.public_key);
    } else {
      // fetch other public key
      const otherId = c.participants.find((p) => p !== user.id);
      const r = await api.get(`/members/${otherId}/public-key`);
      setOtherKey(r.data.public_key);
    }
  };

  // 4. Load + decrypt messages
  useEffect(() => {
    if (!activeConv || !kp) return;
    const load = () => api.get(`/conversations/${activeConv.id}/messages`).then((r) => {
      const dec = r.data.map((m) => {
        if (m.sender_id === user.id) {
          // Pour nos propres messages, on ne peut pas déchiffrer (chiffrés pour l'autre)
          // → on stocke le texte en cache local par id de message
          const cached = localStorage.getItem(`m-${m.id}`);
          return { ...m, text: cached || "(envoyé)" };
        }
        const text = decryptFromSender(m.ciphertext, m.nonce, m.sender_public_key, kp.secretKey);
        return { ...m, text: text || "🔒 Message chiffré (clé indisponible)" };
      });
      setMessages(dec);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    });
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [activeConv, kp, user]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!draft.trim() || !activeConv || !kp || !otherKey) return;
    const { ciphertext, nonce } = encryptForRecipient(draft.trim(), otherKey, kp.secretKey);
    const r = await api.post(`/conversations/${activeConv.id}/messages`, {
      ciphertext, nonce, sender_public_key: kp.publicKeyB64,
    });
    // cache plaintext locally for our own bubble
    localStorage.setItem(`m-${r.data.id}`, draft.trim());
    setDraft("");
    setMessages((m) => [...m, { ...r.data, text: draft.trim() }]);
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  const filteredDir = directory.filter((u) => {
    const q = search.toLowerCase();
    return !q || (u.first_name + " " + u.last_name + " " + (u.email || "")).toLowerCase().includes(q);
  });

  if (!user) return null;

  return (
    <div className="min-h-[calc(100vh-80px)] bg-bone py-6" data-testid="messages-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center gap-3 mb-5">
          <Link to="/membres" className="w-9 h-9 grid place-items-center border border-border hover:border-ink rounded-sm bg-white">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div>
            <div className="eyebrow text-gold-dark flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5" /> Messagerie chiffrée bout-en-bout
            </div>
            <h1 className="font-serif text-2xl text-ink leading-tight">Espace privé</h1>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-4 bg-white border border-border rounded-sm overflow-hidden min-h-[600px]">
          {/* SIDEBAR : contacts + conversations */}
          <aside className="lg:col-span-4 border-r border-border bg-bone/40">
            <div className="p-3 border-b border-border">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40" />
                <input value={search} onChange={(e) => setSearch(e.target.value)}
                       placeholder="Rechercher un membre…"
                       data-testid="messages-search"
                       className="w-full bg-white border border-border rounded-sm pl-10 pr-3 py-2.5 text-sm focus:border-ink outline-none" />
              </div>
            </div>

            {conversations.length > 0 && (
              <div className="px-3 pt-3">
                <div className="eyebrow text-foreground/55 text-[0.55rem] flex items-center gap-1.5">
                  <MessageSquare className="w-3 h-3" /> Conversations
                </div>
                <div className="mt-2 space-y-1">
                  {conversations.map((c) => (
                    <button key={c.id} onClick={() => openConv(c)} data-testid={`conv-${c.id}`}
                            className={`w-full text-left p-2.5 rounded-sm text-sm flex items-center gap-2 transition-colors ${
                              activeConv?.id === c.id ? "bg-ink text-white" : "hover:bg-white"
                            }`}>
                      <div className={`w-8 h-8 rounded-full grid place-items-center text-[0.7rem] font-bold shrink-0 ${
                        activeConv?.id === c.id ? "bg-gold text-ink" : "bg-ink text-gold"
                      }`}>
                        {(c.other?.first_name?.[0] || "?")}{c.other?.last_name?.[0] || ""}
                      </div>
                      <span className="truncate">{c.other?.first_name} {c.other?.last_name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="px-3 py-3 mt-1">
              <div className="eyebrow text-foreground/55 text-[0.55rem] flex items-center gap-1.5">
                <UsersIcon className="w-3 h-3" /> Annuaire ({filteredDir.length})
              </div>
              <div className="mt-2 space-y-1 max-h-[400px] overflow-y-auto">
                {filteredDir.map((m) => (
                  <button key={m.id} onClick={() => openConv(null, m)}
                          disabled={!m.public_key}
                          title={!m.public_key ? "Ce membre n'a pas encore activé la messagerie" : ""}
                          data-testid={`directory-${m.id}`}
                          className="w-full text-left p-2.5 rounded-sm text-sm flex items-center gap-2 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
                    <div className="w-8 h-8 rounded-full bg-foreground/10 grid place-items-center text-[0.65rem] font-bold shrink-0">
                      {m.first_name?.[0]}{m.last_name?.[0]}
                    </div>
                    <div className="min-w-0">
                      <div className="truncate">{m.first_name} {m.last_name}</div>
                      <div className="text-[0.65rem] text-foreground/50 truncate">{m.function || m.role}</div>
                    </div>
                    {m.public_key && <Lock className="w-3 h-3 text-emerald-500 ml-auto shrink-0" title="E2E disponible" />}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* MAIN : messages */}
          <main className="lg:col-span-8 flex flex-col">
            {activeConv ? (
              <>
                <div className="px-5 py-3 border-b border-border bg-bone/30 flex items-center justify-between">
                  <div>
                    <div className="font-serif text-lg text-ink">
                      {activeConv.other?.first_name} {activeConv.other?.last_name}
                    </div>
                    <div className="text-[0.65rem] text-foreground/55 flex items-center gap-1.5">
                      <Lock className="w-3 h-3 text-emerald-500" />
                      Chiffré E2E · empreinte : {shortFingerprint(otherKey)}
                    </div>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-2 bg-bone/20" style={{ minHeight: 400 }}>
                  {messages.length === 0 && (
                    <div className="text-center text-foreground/50 text-sm py-12">Aucun message. Soyez le premier à écrire.</div>
                  )}
                  {messages.map((m) => {
                    const mine = m.sender_id === user.id;
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] px-4 py-2.5 rounded-sm text-sm leading-relaxed ${
                          mine ? "bg-ink text-white" : "bg-white border border-border text-ink"
                        }`}>
                          <div className="whitespace-pre-wrap break-words">{m.text}</div>
                          <div className={`text-[0.6rem] mt-1 ${mine ? "text-white/55" : "text-foreground/40"}`}>
                            {new Date(m.sent_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>
                <form onSubmit={sendMessage} className="border-t border-border p-3 flex items-center gap-2" data-testid="messages-form">
                  <input value={draft} onChange={(e) => setDraft(e.target.value)}
                         placeholder="Votre message (chiffré)…"
                         data-testid="messages-input"
                         className="flex-1 bg-bone/40 border border-border rounded-sm px-3 py-2.5 text-sm focus:border-ink outline-none" />
                  <button type="submit" disabled={!draft.trim()} data-testid="messages-send"
                          className="w-10 h-10 bg-gold text-ink rounded-sm grid place-items-center hover:bg-gold/90 disabled:opacity-50">
                    <Send className="w-4 h-4" />
                  </button>
                </form>
              </>
            ) : (
              <div className="flex-1 grid place-items-center p-10 text-center">
                <div>
                  <Lock className="w-12 h-12 text-gold-dark mx-auto" />
                  <h2 className="font-serif text-2xl text-ink mt-4">Conversations chiffrées</h2>
                  <p className="text-sm text-foreground/65 mt-3 max-w-md leading-relaxed">
                    Sélectionnez un membre pour démarrer une conversation. Les messages sont chiffrés bout-en-bout
                    avec votre clé privée — même les administrateurs ne peuvent pas les lire.
                  </p>
                  <p className="text-[0.65rem] text-foreground/50 mt-4">
                    Empreinte de votre clé publique : <span className="font-mono">{shortFingerprint(kp?.publicKeyB64)}</span>
                  </p>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
